import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { marked } from 'marked';

interface AnalysisResult {
  question: string;
  answer: string;
  analysisType: string;
  keyFindings: string[];
}

@Component({
  selector: 'app-schedule-analysis-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
  ],
  templateUrl: './schedule-analysis-dialog.component.html',
  styleUrls: ['./schedule-analysis-dialog.component.scss']
})
export class ScheduleAnalysisDialogComponent implements OnInit {
  question = '';
  loading = false;
  error: string | null = null;
  analysisResult: AnalysisResult | null = null;

  private apiBaseUrl = environment.apiBaseUrl;

  constructor(
    public dialogRef: MatDialogRef<ScheduleAnalysisDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { scheduleId: number; versionNumber: number },
    private http: HttpClient
  ) {}

  ngOnInit() {
    // Try to restore the last used question from localStorage
    const lastQuestion = localStorage.getItem('lastScheduleAnalysisQuestion');
    if (lastQuestion) {
      this.question = lastQuestion;
    } else if (!this.question) {
      this.question = "Why isn't this schedule working as expected?";
    }
  }

  analyzeSchedule() {
    if (!this.question.trim()) return;

    // Store the question in localStorage before analyzing
    localStorage.setItem('lastScheduleAnalysisQuestion', this.question.trim());

    this.loading = true;
    this.error = null;

    this.http.post<AnalysisResult>(
      `${this.apiBaseUrl}/schedules/${this.data.scheduleId}/versions/${this.data.versionNumber}/analyze`,
      { question: this.question.trim() }
    ).subscribe({
      next: (result) => {
        this.analysisResult = result;
        this.loading = false;
      },
      error: (err) => {
        console.error('Analysis error:', err);
        this.error = err.error?.error || 'Failed to analyze schedule. Please try again.';
        this.loading = false;
      }
    });
  }

  resetAnalysis() {
    this.analysisResult = null;
    this.error = null;
    this.question = '';
  }

  getMarkdownHtml(): string {
    if (!this.analysisResult?.answer) return '';
    
    try {
      const result = marked(this.analysisResult.answer);
      // Handle both sync and async returns from marked
      if (typeof result === 'string') {
        return result;
      } else {
        // If it's a Promise, return the original text as fallback
        console.warn('Marked returned a Promise, falling back to plain text');
        return this.analysisResult.answer;
      }
    } catch (error) {
      console.error('Error parsing markdown:', error);
      return this.analysisResult.answer; // Fallback to plain text
    }
  }

  getAnalysisTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      'feasibility': 'Feasibility Issue',
      'resource_conflict': 'Resource Conflict',
      'constraint_violation': 'Constraint Violation',
      'optimization': 'Optimization',
      'data_issue': 'Data Issue'
    };
    return labels[type] || type;
  }

}
